import {
  ChangeEvent,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "../utils/axios";
import {
  formatChannelOptionLabel,
  sortChannelsByCategoryAndName,
} from "../utils/channelOptions";
import CollapsibleSection from "./CollapsibleSection";
import Spinner from "./Spinner";
import "./LiveNotifications.css";
import { LIVE_NOTIFICATION_FEATURES } from "@apocalypsebot/shared";
import type { LiveNotificationFeatureKey } from "@apocalypsebot/shared";

interface Channel {
  id: string;
  name: string;
  type?: number;
  categoryName?: string | null;
}

interface Role {
  id: string;
  name: string;
}

interface LiveNotification {
  id: number;
  guild_id: string;
  channel_id: string;
  youtube_channel_id: string;
  message: string;
  auto_delete_previous: number;
  show_tags: number;
  auto_edit_end: number;
  is_live: number;
}

interface LiveNotificationsProps {
  guildId: string;
  channels: Channel[];
  roles: Role[];
}

type FeatureKey = LiveNotificationFeatureKey;
const featureKeys = Object.keys(
  LIVE_NOTIFICATION_FEATURES,
) as FeatureKey[];

function LiveNotifications({ guildId, channels, roles }: LiveNotificationsProps) {
  const [notifications, setNotifications] = useState<LiveNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formUsername, setFormUsername] = useState("");
  const [formChannelId, setFormChannelId] = useState("");
  const [formMessage, setFormMessage] = useState(
    "🚨 We are LIVE! Come hang out with us.",
  );
  const [formAutoDelete, setFormAutoDelete] = useState(false);
  const [formShowTags, setFormShowTags] = useState(false);
  const [formAutoEdit, setFormAutoEdit] = useState(false);
  const [selectedRoleMention, setSelectedRoleMention] = useState("");
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testSuccess, setTestSuccess] = useState<
    { text: string; link?: string } | null
  >(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);

  const formToggleState: Record<FeatureKey, [boolean, (value: boolean) => void]> = {
    auto_delete_previous: [formAutoDelete, setFormAutoDelete],
    show_tags: [formShowTags, setFormShowTags],
    auto_edit_end: [formAutoEdit, setFormAutoEdit],
  };

  const textChannels = useMemo(
    () =>
      sortChannelsByCategoryAndName(
        (channels || []).filter(
          (channel) => channel.type === 0 || channel.type === 5,
        ),
      ),
    [channels],
  );

  const sortedRoles = useMemo(
    () => [...(roles || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [roles],
  );

  useEffect(() => {
    fetchNotifications();
  }, [guildId]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/api/server/${guildId}/youtube-live-notifications`,
      );
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to load live notifications", error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormUsername("");
    setFormChannelId("");
    setFormMessage("🚨 We are LIVE! Come hang out with us.");
    setFormAutoDelete(false);
    setFormShowTags(false);
    setFormAutoEdit(false);
    setSelectedRoleMention("");
    setEditingId(null);
    setShowForm(false);
  };

  const startCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = (notification: LiveNotification) => {
    setFormUsername(notification.youtube_channel_id);
    setFormChannelId(notification.channel_id);
    setFormMessage(notification.message);
    setFormAutoDelete(notification.auto_delete_previous === 1);
    setFormShowTags(notification.show_tags === 1);
    setFormAutoEdit(notification.auto_edit_end === 1);
    setSelectedRoleMention("");
    setEditingId(notification.id);
    setShowForm(true);
  };

  const handleRoleMentionSelect = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const roleId = event.target.value;
    if (!roleId) {
      return;
    }

    setSelectedRoleMention(roleId);
    setFormMessage((previous) => {
      const needsSpace = previous.length > 0 && !previous.endsWith(" ");
      return `${previous}${needsSpace ? " " : ""}<@&${roleId}>`;
    });

    setTimeout(() => {
      setSelectedRoleMention("");
      messageInputRef.current?.focus();
    }, 0);
  };

  const handleSendTest = async (notification: LiveNotification) => {
    setTestingId(notification.id);
    setTestError(null);
    setTestSuccess(null);
    try {
      const response = await axios.post(
        `/api/server/${guildId}/youtube-live-notifications/${notification.id}/test`,
      );
      const viewUrl = response.data?.messageUrl as string | undefined;
      setTestSuccess({
        text: `Preview sent to ${getChannelName(notification.channel_id)}.`,
        link: viewUrl,
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to send a preview. Please try again.";
      setTestError(message);
    } finally {
      setTestingId(null);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!formChannelId || !formUsername.trim() || !formMessage.trim()) {
      alert("Please complete every field");
      return;
    }

    setSubmitting(true);

    try {
      if (editingId) {
        await axios.put(
          `/api/server/${guildId}/youtube-live-notifications/${editingId}`,
          {
            channelId: formChannelId,
            youtubeChannelId: formUsername.trim(),
            message: formMessage.trim(),
            autoDeletePrevious: formAutoDelete,
            showTags: formShowTags,
            autoEditEnd: formAutoEdit,
          },
        );
      } else {
        await axios.post(`/api/server/${guildId}/youtube-live-notifications`, {
          channelId: formChannelId,
          youtubeChannelId: formUsername.trim(),
          message: formMessage.trim(),
          autoDeletePrevious: formAutoDelete,
          showTags: formShowTags,
          autoEditEnd: formAutoEdit,
        });
      }

      await fetchNotifications();
      resetForm();
    } catch (error: any) {
      console.error("Failed to save live notification", error);
      alert(
        error.response?.data?.error || "Failed to save live notification. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (
      !confirm(
        "Are you sure you want to remove this automatic live notification?",
      )
    ) {
      return;
    }

    setDeletingId(id);
    try {
      await axios.delete(
        `/api/server/${guildId}/youtube-live-notifications/${id}`,
      );
      await fetchNotifications();
      resetForm();
    } catch (error: any) {
      console.error("Failed to delete live notification", error);
      alert(error.response?.data?.error || "Failed to delete live notification");
    } finally {
      setDeletingId(null);
    }
  };

  const getChannelName = (channelId: string) => {
    const channel = (channels || []).find((c) => c.id === channelId);
    return channel ? formatChannelOptionLabel(channel) : channelId;
  };

  const editingNotification = editingId
    ? notifications.find((notification) => notification.id === editingId)
    : null;

  return (
    <CollapsibleSection
      title="📺 YouTube Live Notifications"
      count={notifications.length}
      defaultCollapsed
    >
      <div className="live-notifications">
        <div className="live-notifications__intro">
          <p>
            Automatically post custom announcements when your favorite YouTube
            channels go live. These settings mirror the <code>/youtube-live</code>
            slash command so admins can manage everything from the dashboard.
          </p>
          <button type="button" className="btn btn-primary" onClick={startCreate}>
            ➕ Add Streamer
          </button>
        </div>

        {testSuccess && (
          <div className="live-feedback live-feedback--success">
            <span>{testSuccess.text}</span>
            {testSuccess.link && (
              <a href={testSuccess.link} target="_blank" rel="noreferrer">
                View message
              </a>
            )}
          </div>
        )}
        {testError && (
          <div className="live-feedback live-feedback--error">{testError}</div>
        )}

        {showForm && (
          <form className="live-form" onSubmit={handleSubmit}>
            <div className="live-form__header">
              <h4>
                {editingId
                  ? "Update YouTube Live Notification"
                  : "Add YouTube Live Notification"}
              </h4>
              <button type="button" className="btn btn-text" onClick={resetForm}>
                ✕ Close
              </button>
            </div>

            <div className="live-form__grid">
              <label>
                YouTube Channel (ID or handle)
                <input
                  type="text"
                  value={formUsername}
                  placeholder="UC-1234567890 or https://www.youtube.com/@Channel"
                  onChange={(event) => setFormUsername(event.target.value)}
                  required
                />
                <span className="live-form__helper-text">
                  Paste a channel ID, handle (e.g. @BocaBola_), or YouTube channel URL.
                </span>
              </label>

              <label>
                Announcement Channel
                <select
                  value={formChannelId}
                  onChange={(event) => setFormChannelId(event.target.value)}
                  required
                >
                  <option value="" disabled>
                    Select a channel
                  </option>
                  {textChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {formatChannelOptionLabel(channel)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="live-form__message-group">
              <label>
                Announcement Message
                <textarea
                  ref={messageInputRef}
                  value={formMessage}
                  onChange={(event) => setFormMessage(event.target.value)}
                  maxLength={200}
                  rows={3}
                  required
                />
              </label>

              {sortedRoles.length > 0 && (
                <label className="live-form__role-picker">
                  Insert Role Mention
                  <select
                    value={selectedRoleMention}
                    onChange={handleRoleMentionSelect}
                  >
                    <option value="">
                      Select a role to mention
                    </option>
                    {sortedRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        @{role.name}
                      </option>
                    ))}
                  </select>
                  <span className="live-form__helper-text">
                    Selecting a role inserts{" "}
                    <code>{"<@&roleId>"}</code> into the message.
                  </span>
                </label>
              )}
            </div>

                        <div className="live-form__toggles">
              {featureKeys.map((feature) => {
                const [checked, setChecked] = formToggleState[feature];
                return (
                  <label key={feature} className="live-form__toggle">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => setChecked(event.target.checked)}
                    />
                    <div className="live-form__toggle-text">
                      <span>{LIVE_NOTIFICATION_FEATURES[feature].label}</span>
                      <p className="live-form__toggle-description">
                        {LIVE_NOTIFICATION_FEATURES[feature].description}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="live-form__actions">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : editingId ? "Update" : "Create"}
              </button>
              {editingNotification && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleSendTest(editingNotification)}
                  disabled={testingId === editingNotification.id}
                >
                  {testingId === editingNotification.id ? "Sending..." : "Send Test"}
                </button>
              )}
              {editingId && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDelete(editingId)}
                  disabled={submitting || deletingId === editingId}
                >
                  {deletingId === editingId ? "Deleting..." : "Delete"}
                </button>
              )}
              <button type="button" className="btn" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <Spinner size="medium" text="Loading live notifications..." />
        ) : notifications.length === 0 ? (
            <p className="empty-message">
              No YouTube live notifications yet. Click “Add Streamer” to get
              started.
            </p>
        ) : (
          <div className="live-table-wrapper">
            <table className="live-table">
              <thead>
                <tr>
                  <th>YouTube channel</th>
                  <th>Stream status</th>
                  <th>Channel</th>
                  <th>Auto delete</th>
                  <th>Tags</th>
                  <th>Edit when ends</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => {
                  return (
                    <tr key={notification.id}>
                      <td className="live-table__user-cell">
                        <div className="live-table__user">
                          <strong>{notification.youtube_channel_id}</strong>
                        </div>
                      </td>
                      <td>
                        <div className="live-table__status">
                          <span
                            className={`status-pill ${
                              notification.is_live
                                ? "status-pill--active"
                                : "status-pill--muted"
                            }`}
                          >
                            {notification.is_live ? "Live" : "Offline"}
                          </span>
                        </div>
                      </td>
                      <td>{getChannelName(notification.channel_id)}</td>
                      <td className="boolean-cell">
                        <span
                          className={`boolean-pill ${
                            notification.auto_delete_previous === 1
                              ? "boolean-pill--yes"
                              : "boolean-pill--no"
                          }`}
                        >
                          {notification.auto_delete_previous === 1 ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="boolean-cell">
                        <span
                          className={`boolean-pill ${
                            notification.show_tags === 1
                              ? "boolean-pill--yes"
                              : "boolean-pill--no"
                          }`}
                        >
                          {notification.show_tags === 1 ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="boolean-cell">
                        <span
                          className={`boolean-pill ${
                            notification.auto_edit_end === 1
                              ? "boolean-pill--yes"
                              : "boolean-pill--no"
                          }`}
                        >
                          {notification.auto_edit_end === 1 ? "Yes" : "No"}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary btn-compact"
                          onClick={() => startEdit(notification)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export default LiveNotifications;


